/**
 * Last-known answers for reads, so a screen renders offline instead of an error.
 *
 * The queue alone is not enough. A teacher in a courtyard with no signal opens
 * the register: without a cached class list and roster there is nothing to mark,
 * and the offline write queue never gets a chance to be useful. The cache is
 * what makes the screen exist; the outbox is what makes the marks survive.
 *
 * CACHED DATA IS ALWAYS LABELLED AS SUCH by the caller. This is a school
 * register and a family's balance: showing yesterday's numbers as if they were
 * today's is how someone acts on a payment that has since been reversed. The
 * cache reports `cachedAt` and the UI says "hors ligne — données du …".
 *
 * Nothing here throws. A cache that breaks the app it is meant to protect is
 * worse than no cache.
 */
import { openDb } from "./db";

/** Older than this and we would rather show nothing than mislead. */
export const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export async function writeCache(path: string, payload: unknown): Promise<void> {
  const db = openDb();
  if (!db) return;
  try {
    await db.reads.put({ path, payload, cached_at: Date.now() });
  } catch { /* storage full or unavailable — the app is unaffected */ }
}

export async function readCache(
  path: string,
): Promise<{ payload: unknown; cachedAt: number } | null> {
  const db = openDb();
  if (!db) return null;
  try {
    const row = await db.reads.get(path);
    if (!row) return null;
    if (Date.now() - row.cached_at > MAX_AGE_MS) return null;
    return { payload: row.payload, cachedAt: row.cached_at };
  } catch {
    return null;
  }
}

/** Signed out, or a different account on this device. */
export async function clearCache(): Promise<void> {
  const db = openDb();
  if (!db) return;
  try {
    await db.reads.clear();
  } catch { /* ignore */ }
}

/**
 * Is this failure a connectivity failure?
 *
 * Duck-typed rather than importing ApiError, so this stays usable from
 * anywhere. `navigator.onLine === false` is trusted when it says offline; it
 * is NOT trusted when it says online, because a captive portal and a dead
 * uplink both report `true`.
 */
export function isOfflineError(error: unknown): boolean {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  const candidate = error as { status?: number; message?: string } | null;
  if (candidate?.status === 0) return true;
  return /NetworkError|Failed to fetch|network:/i.test(String(candidate?.message ?? ""));
}
